import { UIFlightData, UIFlightSegment, UILocation, UIPriceBreakdown } from "@flights/types";
import { pricingUtils } from "@flights/web-api-utils-universal";
import { useSelector } from "react-redux";
import {
  getCabinClass,
  getChildrensAges,
  getFilters,
  getNoOfAdults,
  getPage,
  getSearchSegments,
  getSearchType,
  getSort
} from "../../../store/search/selectors";
import { WishlistPriceDetails, WishlistSearchDetails, WishlistSegmentDetails } from "./types";
import { useMemo } from "react";

export const useFlightWishlistAdaptor = (flight?: UIFlightData) => {
  const searchSegments = useSelector(getSearchSegments);
  const flightType = useSelector(getSearchType);
  const adults = useSelector(getNoOfAdults);
  const cabinClass = useSelector(getCabinClass);
  const sort = useSelector(getSort);
  const children = useSelector(getChildrensAges);
  const page = useSelector(getPage);
  const filters = useSelector(getFilters);

  const flightSegmentAndPriceDetails = useMemo(() => {
    if (!flight) return null;

    const { priceBreakdown, segments } = flight;
    return {
      segments: transformSegmentDetails(segments),
      prices: calculatePrices(priceBreakdown)
    };
  }, [flight]);

  const routes = searchSegments.map(({ departureDate: startDate, returnDate, to, from }) => ({
    startDate,
    returnDate,
    destinationAirports: to.map(mapAirportDetails),
    sourceAirports: from.map(mapAirportDetails)
  }));

  const flightSearchDetails: WishlistSearchDetails = {
    adults,
    cabinClass,
    flightType,
    airlines: filters.airlines,
    stops: filters.stops,
    duration: filters.duration,
    children,
    sort,
    page,
    routes
  };

  return {
    flightSegmentDetails: flightSegmentAndPriceDetails?.segments,
    flightPriceDetails: flightSegmentAndPriceDetails?.prices,
    ...flightSearchDetails
  };
};

export const transformSegmentDetails = (segments: UIFlightSegment[]): WishlistSegmentDetails[] => {
  return segments.map(({ departureAirport, departureTime, arrivalAirport, arrivalTime, totalTime, legs }) => {
    return {
      departureAirportCode: departureAirport.code,
      departureAirportName: departureAirport.name,
      departureCityName: departureAirport.cityName ?? departureAirport.city,
      departureCountryCode: departureAirport.country,
      departureTime: departureTime,
      arrivalAirportCode: arrivalAirport.code,
      arrivalAirportName: arrivalAirport.name,
      arrivalCityName: arrivalAirport.cityName ?? arrivalAirport.city,
      arrivalCountryCode: arrivalAirport.country,
      arrivalTime: arrivalTime,
      travelTime: totalTime,
      stops: legs.length - 1,
      airlines: legs.flatMap((leg) => leg.carriersData)
    };
  });
};

export const calculatePrices = (priceBreakdown: UIPriceBreakdown): WishlistPriceDetails => {
  const totalPrice = pricingUtils.priceToValue(priceBreakdown.total);
  const totalPriceCurrency = priceBreakdown.total.currencyCode;
  const totalPriceWithoutDiscount = priceBreakdown.totalWithoutDiscount
    ? pricingUtils.priceToValue(priceBreakdown.totalWithoutDiscount)
    : undefined;

  return {
    totalPrice,
    totalPriceCurrency,
    totalPriceWithoutDiscount,
    totalPriceWithoutDiscountCurrency: priceBreakdown.totalWithoutDiscount?.currencyCode
  };
};

const mapAirportDetails = (airport: UILocation) => ({
  code: airport.code,
  name: airport.name,
  type: airport.type,
  city: airport.city,
  cityName: airport.cityName,
  country: airport.country,
  countryName: airport.countryName,
  regionName: airport.regionName
});
